import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Radar, Crown } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export const Scanner = ({ onResult, priorityAvailable = 0, onScanDone }) => {
  const [target, setTarget] = useState("");
  const [authorized, setAuthorized] = useState(false);
  const [priority, setPriority] = useState(false);
  const [loading, setLoading] = useState(false);
  const hasCredit = priorityAvailable > 0;

  const run = async (e) => {
    e.preventDefault();
    if (!target.trim()) return toast.error("Introduce un dominio o IP.");
    if (!authorized) return toast.error("Debes confirmar que tienes autorización.");
    setLoading(true);
    try {
      const usePriority = priority && hasCredit;
      const { data } = await api.post("/scan", { target: target.trim(), authorized, priority: usePriority });
      toast.success(
        data.priority
          ? `Análisis contratado activado · Nota ${data.grade}. Te contactaremos para el informe profesional.`
          : `Escaneo completado · Nota ${data.grade}`
      );
      onResult(data);
      onScanDone?.();
      if (usePriority) setPriority(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={run}
      className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 md:p-8"
      data-testid="scanner-form"
    >
      <h2 className="flex items-center gap-2 font-head text-xl font-bold text-white">
        <Radar className="h-5 w-5 text-cyber-cyan" /> Nuevo escaneo
      </h2>
      <p className="mt-2 font-sans text-sm text-white/55">
        Analiza puertos abiertos, cabeceras de seguridad y certificado TLS de tu sistema.
      </p>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row">
        <input
          data-testid="scan-target"
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          placeholder="ejemplo.com  ·  scanme.nmap.org"
          className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3.5 font-mono-tech text-sm text-white placeholder:text-white/30 outline-none transition-colors duration-300 focus:border-cyber-cyan focus:ring-1 focus:ring-cyber-cyan"
        />
        <button
          type="submit"
          disabled={loading}
          data-testid="scan-run"
          className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-cyber-cyan px-8 py-3.5 font-mono-tech text-xs font-bold uppercase tracking-widest text-[#04060a] transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60"
        >
          {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Escaneando</> : "Escanear"}
        </button>
      </div>

      {hasCredit && (
        <label
          className="mt-4 flex cursor-pointer items-center gap-3 rounded-lg border border-cyber-cyan/30 bg-cyber-cyan/[0.06] p-3"
          data-testid="scan-priority"
        >
          <input
            type="checkbox"
            checked={priority}
            onChange={(e) => setPriority(e.target.checked)}
            className="h-4 w-4 accent-cyber-cyan"
          />
          <span className="flex items-center gap-2 font-sans text-xs text-white/80">
            <Crown className="h-4 w-4 text-cyber-cyan" />
            Usar mi <b>análisis contratado</b> (prioritario) · {priorityAvailable} disponible{priorityAvailable > 1 ? "s" : ""}
          </span>
        </label>
      )}

      <label className="mt-4 flex cursor-pointer items-start gap-3" data-testid="scan-authorize">
        <input
          type="checkbox"
          checked={authorized}
          onChange={(e) => setAuthorized(e.target.checked)}
          className="mt-0.5 h-4 w-4 accent-cyber-cyan"
        />
        <span className="font-sans text-xs leading-relaxed text-white/55">
          Confirmo que soy propietario del sistema objetivo o cuento con autorización expresa para escanearlo. El escaneo no autorizado de sistemas ajenos es ilegal.
        </span>
      </label>
    </form>
  );
};
